import type { Post } from "@/types";
import type { RouteLocationRaw } from "vue-router";
import DOMPurify from "dompurify";
import { generateHTML } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { MentionExtension } from "@/components/editor/extensions/MentionExtension";
import { SongReferenceExtension } from "@/components/editor/extensions/SongReferenceExtension";

/**
 * Get the route for a post detail page based on post type
 */
export function getPostRoute(post: Post): RouteLocationRaw {
  const ulid = post.ulid || post.id;
  const postType = (post.type || post.postType || "POST").toUpperCase();

  switch (postType) {
    case "SONG":
      return { name: "song-detail", params: { ulid } };
    case "VIDEO":
      return { name: "video-detail", params: { ulid } };
    case "SHORT":
      return { name: "short-detail", params: { ulid } };
    case "PODCAST":
      return { name: "podcast-detail", params: { ulid } };
    case "BURST":
      return { name: "burst-detail", params: { ulid } };
    default:
      return { name: "post-detail", params: { ulid } };
  }
}

/**
 * Get the URL path for a post based on type
 */
export function getPostPath(post: Post): string {
  const ulid = post.ulid || post.id;
  const postType = (post.type || post.postType || "POST").toUpperCase();

  switch (postType) {
    case "SONG":
      return `/song/${ulid}`;
    case "VIDEO":
      return `/v/${ulid}`;
    case "SHORT":
      return `/s/${ulid}`;
    case "PODCAST":
      return `/podcast/${ulid}`;
    case "BURST":
      return `/burst/${ulid}`;
    default:
      return `/post/${ulid}`;
  }
}

/**
 * Get the detail panel title based on post type
 */
export function getDetailTitle(post: Post | null): string {
  if (!post?.type) return "Post Details";
  const typeMap: Record<string, string> = {
    SONG: "Song Details",
    VIDEO: "Video Details",
    SHORT: "Short Details",
    PODCAST: "Podcast Details",
    BURST: "Burst Details",
    POST: "Post Details",
    REPLY: "Reply Details",
    REPOST: "Repost Details",
    QUOTE: "Quote Details",
  };
  return typeMap[post.type.toUpperCase()] || "Post Details";
}

/**
 * Get the icon for a post type
 */
export function getPostTypeIcon(type: string | undefined): string {
  if (!type) return "mdi-post-outline";
  const iconMap: Record<string, string> = {
    SONG: "mdi-music",
    VIDEO: "mdi-video",
    SHORT: "mdi-video-box",
    PODCAST: "mdi-podcast",
    BURST: "mdi-lightning-bolt",
    POST: "mdi-post-outline",
    REPLY: "mdi-comment",
    REPOST: "mdi-repeat",
    QUOTE: "mdi-format-quote-close",
  };
  return iconMap[type.toUpperCase()] || "mdi-post-outline";
}

/**
 * Get a human-readable label for a post type
 */
export function getPostTypeLabel(type: string | undefined): string {
  if (!type) return "Post";
  const labelMap: Record<string, string> = {
    SONG: "Song",
    VIDEO: "Video",
    SHORT: "Short",
    PODCAST: "Podcast",
    BURST: "Burst",
    POST: "Post",
    REPLY: "Reply",
    REPOST: "Repost",
    QUOTE: "Quote",
  };
  return labelMap[type.toUpperCase()] || "Post";
}

/**
 * Create a song reference string in the format #Slugified-Title[song:ULID]
 * This format is parsed by formatBodyWithLinks to create direct links to songs
 */
export function createSongReference(title: string, ulid: string): string {
  const slugifiedTitle = title
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
  return `#${slugifiedTitle}[song:${ulid}]`;
}

/**
 * Check if content appears to be TipTap JSON ({"type":"doc",...})
 */
function isTipTapJson(text: string): boolean {
  if (!text.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(text);
    return parsed?.type === "doc" && Array.isArray(parsed?.content);
  } catch {
    return false;
  }
}

/**
 * TipTap extensions used for generateHTML (matches the editor config)
 */
const tiptapExtensions = [
  StarterKit.configure({ heading: false, codeBlock: false }),
  Underline,
  Link.configure({ openOnClick: false }),
  MentionExtension,
  SongReferenceExtension,
];

/**
 * Convert TipTap JSON string to HTML using the same extensions as the editor
 */
function tiptapJsonToHtml(text: string): string {
  try {
    const json = JSON.parse(text);
    return generateHTML(json, tiptapExtensions);
  } catch {
    return text;
  }
}

/**
 * Check if content appears to be HTML (from rich text editor)
 */
function isHtmlContent(text: string): boolean {
  // Check for common TipTap/HTML tags
  return /<(p|div|span|strong|em|u|s|a|ul|ol|li|blockquote|br)\b[^>]*>/i.test(text);
}

/**
 * Convert TipTap mention spans to clickable links
 * Input: <span data-type="mention" class="mention" data-id="ULID" data-label="username">@username</span>
 * Output: <a href="/profile/username" class="mention" onclick="event.stopPropagation()">@username</a>
 */
function convertMentionSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="mention"[^>]*data-label="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/profile/$1" class="mention" onclick="event.stopPropagation()">$2</a>'
  );
}

/**
 * Convert TipTap song reference spans to clickable links
 * Input: <span data-type="songReference" class="song-reference" data-id="ULID" data-title="Title">#Title</span>
 * Output: <a href="/song/ULID" class="song-reference" onclick="event.stopPropagation()">#Title</a>
 */
function convertSongReferenceSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="songReference"[^>]*data-id="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/song/$1" class="song-reference" onclick="event.stopPropagation()">$2</a>'
  );
}

/**
 * Format post body text with clickable @mentions and #hashtags
 * For plain text: escapes HTML to prevent XSS and converts mentions/hashtags to links
 * For HTML content (from rich text editor): sanitizes with DOMPurify and converts TipTap nodes to links
 * - @username -> link to /profile/username
 * - #hashtag[song:ULID] -> link to /song/ULID (song reference)
 * - #hashtag -> link to /search?q=%23hashtag (regular hashtag)
 */
export function formatBodyWithLinks(
  text: string,
  mentionClass = "mention",
  hashtagClass = "hashtag"
): string {
  if (!text) return "";

  // If content is TipTap JSON, convert to HTML first
  if (isTipTapJson(text)) {
    text = tiptapJsonToHtml(text);
  }

  // If content is HTML (from rich text editor or converted from TipTap JSON), sanitize and convert nodes to links
  if (isHtmlContent(text)) {
    // Sanitize HTML to prevent XSS
    // Note: onclick is intentionally NOT allowed here to prevent XSS attacks
    // The onclick="event.stopPropagation()" is added by our code AFTER sanitization
    let sanitized = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'span', 'ul', 'ol', 'li', 'blockquote'],
      ALLOWED_ATTR: ['href', 'class', 'data-type', 'data-id', 'data-label', 'data-title', 'target', 'rel'],
    });

    sanitized = formatHashtagsAndMentions(sanitized, mentionClass, hashtagClass);

    // Convert TipTap mention/song-reference spans to clickable links
    sanitized = convertMentionSpansToLinks(sanitized);
    sanitized = convertSongReferenceSpansToLinks(sanitized);

    return sanitized;
  }

  // For plain text, escape HTML to prevent XSS
  let formatted = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\\n/g, "\n");

  formatted = formatHashtagsAndMentions(formatted, mentionClass, hashtagClass);

  return formatted;
}

function formatHashtagsAndMentions(text: string, mentionClass: string, hashtagClass: string): string {
  let formatted = text

  // Replace @mentions with links
  formatted = formatted.replace(
    /@([a-zA-Z0-9_]+)/g,
    `<a href="/profile/$1" class="${mentionClass}" onclick="event.stopPropagation()">@$1</a>`
  );

  // Replace #hashtag[song:ULID] with links to song pages
  formatted = formatted.replace(
    /#([\w-]+)\[song:([a-zA-Z0-9]+)\]/g,
    `<a href="/song/$2" class="${hashtagClass}" onclick="event.stopPropagation()">#$1</a>`
  );

  // Replace regular #hashtags with search links (including hyphens which are common in hashtags)
  // Use negative lookbehind (?<!>) to avoid matching hashtags already inside anchor tags from song reference replacement
  formatted = formatted.replace(
    /(?<!>)#([\w-]+)/g,
    `<a href="/search?q=%23$1" class="${hashtagClass}" onclick="event.stopPropagation()">#$1</a>`
  );

  return formatted;
}

/**
 * YouTube video info extracted from a URL
 */
export interface YouTubeVideoInfo {
  videoId: string;
  url: string;
  thumbnailUrl: string;
  embedUrl: string;
}

/**
 * Extract YouTube video ID from various URL formats
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Standard youtube.com/watch?v= format
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];

  // Short youtu.be/ format
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];

  // Embed format youtube.com/embed/
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];

  // Shorts format youtube.com/shorts/
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return shortsMatch[1];

  return null;
}

/**
 * Extract YouTube video info from text content
 * Returns the first YouTube link found in the text
 */
export function extractYouTubeFromText(text: string): YouTubeVideoInfo | null {
  if (!text) return null;

  // Match YouTube URLs in text
  const urlRegex = /https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?[^\s]*v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*/gi;
  const match = urlRegex.exec(text);

  if (match) {
    const videoId = extractYouTubeVideoId(match[0]);
    if (videoId) {
      return {
        videoId,
        url: match[0],
        // Use maxresdefault with fallback to hqdefault (some videos don't have maxres)
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
      };
    }
  }

  return null;
}

/**
 * Get the canonical YouTube watch URL for a video
 */
export function getYouTubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Remove YouTube URLs from text content
 * Used to hide the raw URL when displaying a YouTube preview
 */
export function stripYouTubeUrls(text: string): string {
  if (!text) return "";

  // Remove YouTube URLs (supports all formats including youtu.be short links)
  return text
    .replace(/https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?[^\s]*|embed\/[^\s]*|shorts\/[^\s]*)|youtu\.be\/[^\s]*)/gi, "")
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();
}

// ============================================================
// SPOTIFY EMBED UTILITIES
// ============================================================

export type SpotifyContentType = 'track' | 'album' | 'playlist' | 'artist' | 'episode' | 'show';

export interface SpotifyEmbedInfo {
  type: SpotifyContentType;
  id: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Spotify content info from URL
 * Supports: open.spotify.com/track|album|playlist|artist|episode|show/ID
 */
export function extractSpotifyFromText(text: string): SpotifyEmbedInfo | null {
  if (!text) return null;

  // Match Spotify URLs - open.spotify.com/{type}/{id}
  const regex = /https?:\/\/open\.spotify\.com\/(track|album|playlist|artist|episode|show)\/([a-zA-Z0-9]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const type = match[1].toLowerCase() as SpotifyContentType;
    const id = match[2];
    return {
      type,
      id,
      url: match[0],
      embedUrl: `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`,
    };
  }

  return null;
}

/**
 * Remove Spotify URLs from text
 */
export function stripSpotifyUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/open\.spotify\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// SOUNDCLOUD EMBED UTILITIES
// ============================================================

export interface SoundCloudEmbedInfo {
  url: string;
  embedUrl: string;
}

/**
 * Extract SoundCloud info from URL
 * SoundCloud requires oEmbed API call to get embed HTML, but we can detect the URL
 * Note: Only full URLs (soundcloud.com) work with the widget embed.
 * Short URLs (on.soundcloud.com) don't work with the widget - they need to be resolved first.
 */
export function extractSoundCloudFromText(text: string): SoundCloudEmbedInfo | null {
  if (!text) return null;

  // Match full SoundCloud URLs (soundcloud.com/artist/track) - these work with the widget
  const fullRegex = /https?:\/\/(?:www\.)?soundcloud\.com\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+[^\s]*/gi;
  const match = fullRegex.exec(text);

  if (match) {
    const url = match[0];
    // SoundCloud widget URL with encoded track URL
    const params = `color=%23ff5500&auto_play=false&hide_related=true`
      + `&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;
    const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&${params}`;
    return { url, embedUrl };
  }

  // Short URLs (on.soundcloud.com) don't work with widget, so we don't extract them for embedding
  // They will just appear as regular links in the post text

  return null;
}

/**
 * Remove SoundCloud URLs from text (both full and short URLs)
 */
export function stripSoundCloudUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?soundcloud\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/on\.soundcloud\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TWITTER/X EMBED UTILITIES
// ============================================================

export interface TwitterEmbedInfo {
  tweetId: string;
  username: string;
  url: string;
}

/**
 * Extract Twitter/X tweet info from URL
 * Supports: twitter.com and x.com
 */
export function extractTwitterFromText(text: string): TwitterEmbedInfo | null {
  if (!text) return null;

  // Match Twitter/X URLs - both twitter.com and x.com
  const regex = /https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)\/status\/(\d+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      username: match[1],
      tweetId: match[2],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Twitter/X URLs from text
 */
export function stripTwitterUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// INSTAGRAM EMBED UTILITIES
// ============================================================

export interface InstagramEmbedInfo {
  type: 'post' | 'reel';
  shortcode: string;
  url: string;
}

/**
 * Extract Instagram post/reel info from URL
 */
export function extractInstagramFromText(text: string): InstagramEmbedInfo | null {
  if (!text) return null;

  // Match Instagram post URLs - instagram.com/p/{shortcode} or /reel/{shortcode}
  const regex = /https?:\/\/(?:www\.)?instagram\.com\/(p|reel)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      type: match[1] === 'reel' ? 'reel' : 'post',
      shortcode: match[2],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Instagram URLs from text
 */
export function stripInstagramUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?instagram\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TIKTOK EMBED UTILITIES
// ============================================================

export interface TikTokEmbedInfo {
  videoId: string;
  username: string;
  url: string;
}

/**
 * Extract TikTok video info from URL
 * Supports: tiktok.com/@user/video/ID and vm.tiktok.com short links
 */
export function extractTikTokFromText(text: string): TikTokEmbedInfo | null {
  if (!text) return null;

  // Match TikTok URLs - tiktok.com/@username/video/videoId
  const regex = /https?:\/\/(?:www\.)?tiktok\.com\/@([a-zA-Z0-9_.]+)\/video\/(\d+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      username: match[1],
      videoId: match[2],
      url: match[0],
    };
  }

  // Also check for vm.tiktok.com short links (we'll just detect them, actual ID requires redirect)
  const shortRegex = /https?:\/\/vm\.tiktok\.com\/[a-zA-Z0-9]+[^\s]*/gi;
  const shortMatch = shortRegex.exec(text);

  if (shortMatch) {
    return {
      username: '',
      videoId: '',
      url: shortMatch[0],
    };
  }

  return null;
}

/**
 * Remove TikTok URLs from text
 */
export function stripTikTokUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?tiktok\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/vm\.tiktok\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// VIMEO EMBED UTILITIES (Medium Priority)
// ============================================================

export interface VimeoEmbedInfo {
  videoId: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Vimeo video info from URL
 * Supports: vimeo.com/{id} and player.vimeo.com/video/{id}
 */
export function extractVimeoFromText(text: string): VimeoEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const videoId = match[1];
    return {
      videoId,
      url: match[0],
      embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=0&title=0&byline=0&portrait=0`,
    };
  }

  return null;
}

/**
 * Remove Vimeo URLs from text
 */
export function stripVimeoUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TWITCH EMBED UTILITIES (Medium Priority)
// ============================================================

export interface TwitchEmbedInfo {
  type: 'channel' | 'video' | 'clip';
  id: string;
  url: string;
}

/**
 * Extract Twitch info from URL
 * Supports: twitch.tv/{channel}, twitch.tv/videos/{id}, clips.twitch.tv/{slug}
 */
export function extractTwitchFromText(text: string): TwitchEmbedInfo | null {
  if (!text) return null;

  // Video URL
  const videoRegex = /https?:\/\/(?:www\.)?twitch\.tv\/videos\/(\d+)[^\s]*/gi;
  const videoMatch = videoRegex.exec(text);
  if (videoMatch) {
    return { type: 'video', id: videoMatch[1], url: videoMatch[0] };
  }

  // Clip URL (clips.twitch.tv)
  const clipRegex = /https?:\/\/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const clipMatch = clipRegex.exec(text);
  if (clipMatch) {
    return { type: 'clip', id: clipMatch[1], url: clipMatch[0] };
  }

  // Clip URL (twitch.tv/{channel}/clip/{slug})
  const clipRegex2 = /https?:\/\/(?:www\.)?twitch\.tv\/[a-zA-Z0-9_]+\/clip\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const clipMatch2 = clipRegex2.exec(text);
  if (clipMatch2) {
    return { type: 'clip', id: clipMatch2[1], url: clipMatch2[0] };
  }

  // Channel URL (must be last to avoid matching video/clip URLs)
  const channelRegex = /https?:\/\/(?:www\.)?twitch\.tv\/([a-zA-Z0-9_]+)(?:\?[^\s]*)?(?:$|\s)/gi;
  const channelMatch = channelRegex.exec(text);
  if (channelMatch) {
    return { type: 'channel', id: channelMatch[1], url: channelMatch[0].trim() };
  }

  return null;
}

/**
 * Remove Twitch URLs from text
 */
export function stripTwitchUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?twitch\.tv\/[^\s]+/gi, "")
    .replace(/https?:\/\/clips\.twitch\.tv\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// APPLE MUSIC EMBED UTILITIES (Medium Priority)
// ============================================================

export type AppleMusicContentType = 'album' | 'playlist' | 'song' | 'music-video' | 'artist';

export interface AppleMusicEmbedInfo {
  type: AppleMusicContentType;
  country: string;
  id: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Apple Music info from URL
 * Supports: music.apple.com/{country}/{type}/{name}/{id}
 */
export function extractAppleMusicFromText(text: string): AppleMusicEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/music\.apple\.com\/([a-z]{2})\/(album|playlist|song|music-video|artist)\/[^\/]+\/([a-zA-Z0-9.]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const country = match[1];
    const type = match[2] as AppleMusicContentType;
    const id = match[3];
    return {
      type,
      country,
      id,
      url: match[0],
      embedUrl: `https://embed.music.apple.com/${country}/${type}/${id}`,
    };
  }

  return null;
}

/**
 * Remove Apple Music URLs from text
 */
export function stripAppleMusicUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/music\.apple\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// BANDCAMP EMBED UTILITIES (Medium Priority)
// ============================================================

export interface BandcampEmbedInfo {
  type: 'track' | 'album';
  artist: string;
  slug: string;
  url: string;
}

/**
 * Extract Bandcamp info from URL
 * Supports: {artist}.bandcamp.com/track/{slug} and {artist}.bandcamp.com/album/{slug}
 */
export function extractBandcampFromText(text: string): BandcampEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/([a-zA-Z0-9_-]+)\.bandcamp\.com\/(track|album)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      type: match[2] as 'track' | 'album',
      artist: match[1],
      slug: match[3],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Bandcamp URLs from text
 */
export function stripBandcampUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/[a-zA-Z0-9_-]+\.bandcamp\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// FACEBOOK EMBED UTILITIES (Low Priority)
// ============================================================

export interface FacebookEmbedInfo {
  type: 'post' | 'video' | 'watch';
  url: string;
}

/**
 * Extract Facebook post/video info from URL
 */
export function extractFacebookFromText(text: string): FacebookEmbedInfo | null {
  if (!text) return null;

  // Facebook Watch video
  const watchRegex = /https?:\/\/(?:www\.)?facebook\.com\/watch\/?\?v=(\d+)[^\s]*/gi;
  const watchMatch = watchRegex.exec(text);
  if (watchMatch) {
    return { type: 'watch', url: watchMatch[0] };
  }

  // Facebook video
  const videoRegex = /https?:\/\/(?:www\.)?facebook\.com\/[a-zA-Z0-9.]+\/videos\/[^\s]+/gi;
  const videoMatch = videoRegex.exec(text);
  if (videoMatch) {
    return { type: 'video', url: videoMatch[0] };
  }

  // Facebook post
  const postRegex = /https?:\/\/(?:www\.)?facebook\.com\/[a-zA-Z0-9.]+\/posts\/[^\s]+/gi;
  const postMatch = postRegex.exec(text);
  if (postMatch) {
    return { type: 'post', url: postMatch[0] };
  }

  return null;
}

/**
 * Remove Facebook URLs from text
 */
export function stripFacebookUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?facebook\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// LINKEDIN EMBED UTILITIES (Low Priority)
// ============================================================

export interface LinkedInEmbedInfo {
  type: 'post' | 'article';
  url: string;
}

/**
 * Extract LinkedIn post/article info from URL
 */
export function extractLinkedInFromText(text: string): LinkedInEmbedInfo | null {
  if (!text) return null;

  // LinkedIn article
  const articleRegex = /https?:\/\/(?:www\.)?linkedin\.com\/pulse\/[^\s]+/gi;
  const articleMatch = articleRegex.exec(text);
  if (articleMatch) {
    return { type: 'article', url: articleMatch[0] };
  }

  // LinkedIn post
  const postRegex = /https?:\/\/(?:www\.)?linkedin\.com\/(?:feed\/update\/|posts\/)[^\s]+/gi;
  const postMatch = postRegex.exec(text);
  if (postMatch) {
    return { type: 'post', url: postMatch[0] };
  }

  return null;
}

/**
 * Remove LinkedIn URLs from text
 */
export function stripLinkedInUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?linkedin\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// PINTEREST EMBED UTILITIES (Low Priority)
// ============================================================

export interface PinterestEmbedInfo {
  type: 'pin' | 'board';
  id: string;
  url: string;
}

/**
 * Extract Pinterest pin/board info from URL
 */
export function extractPinterestFromText(text: string): PinterestEmbedInfo | null {
  if (!text) return null;

  // Pinterest pin
  const pinRegex = /https?:\/\/(?:www\.)?pinterest\.com\/pin\/(\d+)[^\s]*/gi;
  const pinMatch = pinRegex.exec(text);
  if (pinMatch) {
    return { type: 'pin', id: pinMatch[1], url: pinMatch[0] };
  }

  // Pinterest board
  const boardRegex = /https?:\/\/(?:www\.)?pinterest\.com\/([a-zA-Z0-9_]+)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const boardMatch = boardRegex.exec(text);
  if (boardMatch) {
    return { type: 'board', id: `${boardMatch[1]}/${boardMatch[2]}`, url: boardMatch[0] };
  }

  return null;
}

/**
 * Remove Pinterest URLs from text
 */
export function stripPinterestUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?pinterest\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// REDDIT EMBED UTILITIES (Low Priority)
// ============================================================

export interface RedditEmbedInfo {
  subreddit: string;
  postId: string;
  url: string;
}

/**
 * Extract Reddit post info from URL
 */
export function extractRedditFromText(text: string): RedditEmbedInfo | null {
  if (!text) return null;

  // Reddit post URL
  const regex = /https?:\/\/(?:www\.)?reddit\.com\/r\/([a-zA-Z0-9_]+)\/comments\/([a-zA-Z0-9]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      subreddit: match[1],
      postId: match[2],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Reddit URLs from text
 */
export function stripRedditUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?reddit\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// MIXCLOUD EMBED UTILITIES (Low Priority)
// ============================================================

export interface MixcloudEmbedInfo {
  username: string;
  slug: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Mixcloud info from URL
 * Supports: mixcloud.com/{username}/{slug}
 */
export function extractMixcloudFromText(text: string): MixcloudEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/(?:www\.)?mixcloud\.com\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const username = match[1];
    const slug = match[2];
    return {
      username,
      slug,
      url: match[0],
      embedUrl: `https://www.mixcloud.com/widget/iframe/?feed=%2F${username}%2F${slug}%2F&hide_cover=1`,
    };
  }

  return null;
}

/**
 * Remove Mixcloud URLs from text
 */
export function stripMixcloudUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?mixcloud\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// DAILYMOTION EMBED UTILITIES (Low Priority)
// ============================================================

export interface DailymotionEmbedInfo {
  videoId: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Dailymotion video info from URL
 * Supports: dailymotion.com/video/{id} and dai.ly/{id}
 */
export function extractDailymotionFromText(text: string): DailymotionEmbedInfo | null {
  if (!text) return null;

  // Full URL format
  const fullRegex = /https?:\/\/(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)[^\s]*/gi;
  const fullMatch = fullRegex.exec(text);
  if (fullMatch) {
    const videoId = fullMatch[1];
    return {
      videoId,
      url: fullMatch[0],
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
    };
  }

  // Short URL format
  const shortRegex = /https?:\/\/dai\.ly\/([a-zA-Z0-9]+)[^\s]*/gi;
  const shortMatch = shortRegex.exec(text);
  if (shortMatch) {
    const videoId = shortMatch[1];
    return {
      videoId,
      url: shortMatch[0],
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
    };
  }

  return null;
}

/**
 * Remove Dailymotion URLs from text
 */
export function stripDailymotionUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?dailymotion\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/dai\.ly\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// UNIFIED EMBED DETECTION
// ============================================================

export type EmbedType =
  | 'youtube' | 'spotify' | 'soundcloud' | 'twitter' | 'instagram' | 'tiktok'
  | 'vimeo' | 'twitch' | 'applemusic' | 'bandcamp'
  | 'facebook' | 'linkedin' | 'pinterest' | 'reddit' | 'mixcloud' | 'dailymotion';

export type EmbedData =
  | YouTubeVideoInfo | SpotifyEmbedInfo | SoundCloudEmbedInfo | TwitterEmbedInfo | InstagramEmbedInfo | TikTokEmbedInfo
  | VimeoEmbedInfo | TwitchEmbedInfo | AppleMusicEmbedInfo | BandcampEmbedInfo
  | FacebookEmbedInfo | LinkedInEmbedInfo | PinterestEmbedInfo | RedditEmbedInfo | MixcloudEmbedInfo | DailymotionEmbedInfo;

export interface EmbedInfo {
  type: EmbedType;
  data: EmbedData;
}

/**
 * Detect and extract the first embeddable content from text
 * Returns the first match found in priority order
 */
export function extractEmbedFromText(text: string): EmbedInfo | null {
  if (!text) return null;

  // High priority - video/music platforms
  const youtube = extractYouTubeFromText(text);
  if (youtube) return { type: 'youtube', data: youtube };

  const spotify = extractSpotifyFromText(text);
  if (spotify) return { type: 'spotify', data: spotify };

  const soundcloud = extractSoundCloudFromText(text);
  if (soundcloud) return { type: 'soundcloud', data: soundcloud };

  const twitter = extractTwitterFromText(text);
  if (twitter) return { type: 'twitter', data: twitter };

  const instagram = extractInstagramFromText(text);
  if (instagram) return { type: 'instagram', data: instagram };

  const tiktok = extractTikTokFromText(text);
  if (tiktok) return { type: 'tiktok', data: tiktok };

  // Medium priority - alternative video/music platforms
  const vimeo = extractVimeoFromText(text);
  if (vimeo) return { type: 'vimeo', data: vimeo };

  const twitch = extractTwitchFromText(text);
  if (twitch) return { type: 'twitch', data: twitch };

  const applemusic = extractAppleMusicFromText(text);
  if (applemusic) return { type: 'applemusic', data: applemusic };

  const bandcamp = extractBandcampFromText(text);
  if (bandcamp) return { type: 'bandcamp', data: bandcamp };

  // Low priority - other social platforms
  const facebook = extractFacebookFromText(text);
  if (facebook) return { type: 'facebook', data: facebook };

  const linkedin = extractLinkedInFromText(text);
  if (linkedin) return { type: 'linkedin', data: linkedin };

  const pinterest = extractPinterestFromText(text);
  if (pinterest) return { type: 'pinterest', data: pinterest };

  const reddit = extractRedditFromText(text);
  if (reddit) return { type: 'reddit', data: reddit };

  const mixcloud = extractMixcloudFromText(text);
  if (mixcloud) return { type: 'mixcloud', data: mixcloud };

  const dailymotion = extractDailymotionFromText(text);
  if (dailymotion) return { type: 'dailymotion', data: dailymotion };

  return null;
}

/**
 * Strip all embed URLs from text based on detected embed type
 */
export function stripEmbedUrls(text: string, embedType: EmbedType): string {
  switch (embedType) {
    // High priority
    case 'youtube':
      return stripYouTubeUrls(text);
    case 'spotify':
      return stripSpotifyUrls(text);
    case 'soundcloud':
      return stripSoundCloudUrls(text);
    case 'twitter':
      return stripTwitterUrls(text);
    case 'instagram':
      return stripInstagramUrls(text);
    case 'tiktok':
      return stripTikTokUrls(text);
    // Medium priority
    case 'vimeo':
      return stripVimeoUrls(text);
    case 'twitch':
      return stripTwitchUrls(text);
    case 'applemusic':
      return stripAppleMusicUrls(text);
    case 'bandcamp':
      return stripBandcampUrls(text);
    // Low priority
    case 'facebook':
      return stripFacebookUrls(text);
    case 'linkedin':
      return stripLinkedInUrls(text);
    case 'pinterest':
      return stripPinterestUrls(text);
    case 'reddit':
      return stripRedditUrls(text);
    case 'mixcloud':
      return stripMixcloudUrls(text);
    case 'dailymotion':
      return stripDailymotionUrls(text);
    default:
      return text;
  }
}
